import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import api from "../utils/api";
import ReviewsBar from "../components/ReviewsBar";
import LiveStrip from "../components/LiveStrip";
import "../styles/parkingVsTaxi.css";

export default function GatwickDropoff() {
  const [form, setForm] = useState({
    terminal: "NORTH",
    journey_type: "DROPOFF", // DROPOFF | PICKUP
    planned_minutes: 8,
    vehicle_type: "car", // car | taxi_phv | coach_bus | motorcycle | recovery | emergency | military
    blue_badge: false,
    blue_badge_registered: false
  });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const autoSubmitted = useRef(false);
  const changeTimer = useRef(null);

  // Prefill defaults + auto-run (same style as your Train tool)
  useEffect(() => {
    setForm(prev => ({
      ...prev,
      terminal: "NORTH",
      journey_type: "DROPOFF",
      planned_minutes: 8,
      vehicle_type: "car",
      blue_badge: false,
      blue_badge_registered: false
    }));
  }, []);

  useEffect(() => {
    if (autoSubmitted.current) return;
    // run once after initial prefill
    autoSubmitted.current = true;
    submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!autoSubmitted.current) return;
    if (changeTimer.current) clearTimeout(changeTimer.current);
    changeTimer.current = setTimeout(() => {
      submit();
    }, 320);
    return () => {
      if (changeTimer.current) clearTimeout(changeTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const submit = async () => {
    setLoading(true);
    setError(null);

    try {
      const apiTerminal = "T5"; // backend endpoint expects this placeholder; Gatwick display handled separately
      const payload = {
        terminal: apiTerminal,
        journey_type: "DROPOFF",
        vehicle_type: "car",
        blue_badge: form.blue_badge,
        blue_badge_registered: form.blue_badge_registered,
        overheight: false,
        planned_minutes: clampMinutes(form.planned_minutes)
      };

      const res = await api.post("/api/heathrow-dropoff/calc", payload);
      setResult(res.data);
    } catch (err) {
      setError("Failed to calculate drop-off costs. Please try again.");
    }

    setLoading(false);
  };

  const money = v => (v == null ? "—" : `£${Number(v).toFixed(2)}`);

  const badge = (text, tone = "muted") => (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 12,
        marginLeft: 8,
        border: "1px solid #e5e7eb",
        background: tone === "warn" ? "#fff7ed" : tone === "ok" ? "#ecfdf5" : "#f9fafb",
        color: "#111827"
      }}
    >
      {text}
    </span>
  );

  const optionLabel = (id) => {
    if (id === "DROPOFF_ZONE") return "Terminal drop-off";
    if (id === "SHORT_STAY") return "Short Stay parking";
    if (id === "PARK_RIDE") return "Park & Ride / Long Stay";
    return id || "—";
  };

  const optionTagline = (id) => {
    if (id === "DROPOFF_ZONE") return "Fastest option (PCN risk if overstaying)";
    if (id === "SHORT_STAY") return "Best for pick-ups / waiting near terminal";
    if (id === "PARK_RIDE") return "Free drop-off + shuttle to terminal";
    return "Compare by price & hassle";
  };

  const clampMinutes = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return 8;
    return Math.min(10080, Math.max(1, num));
  };

  const renderMedalHero = () => {
    const palette = {
      GOLD: { bg: "#0b172a", text: "#5eead4", border: "#22d3ee", emoji: "🥇" },
      SILVER: { bg: "#1f2937", text: "#e5e7eb", border: "#cbd5e1", emoji: "🥈" },
      BRONZE: { bg: "#2b1b0f", text: "#fdba74", border: "#fb923c", emoji: "🥉" }
    };

    const options = Array.isArray(result?.options) ? result.options : [];
    const numMinutes = (() => {
      const fromResult = Number(result?.journey?.planned_minutes);
      if (Number.isFinite(fromResult)) return fromResult;
      const fromForm = Number(form.planned_minutes);
      if (Number.isFinite(fromForm)) return fromForm;
      return 0;
    })();

    const byId = options.reduce((acc, opt) => ({ ...acc, [opt.id]: opt }), {});
    const isExempt = form.blue_badge && form.blue_badge_registered && numMinutes <= 30;

    const baseOption = (id) => {
      const raw = byId[id];
      if (!raw) return null;
      let warnings = Array.isArray(raw.warnings) ? [...raw.warnings] : [];
      const infoNotes = [];

      if (id === "DROPOFF_ZONE") {
        warnings = [];
        const mins = Math.min(numMinutes, 30);
        const extra1 = Math.max(0, Math.min(mins, 20) - 10); // £1/min 11-20
        const extra2 = Math.max(0, mins - 20); // minutes 21-30
        const gatwickCost = Math.min(30, 10 + extra1 + extra2);
        raw.cost = isExempt ? 0 : gatwickCost;
        if (isExempt) {
          warnings = [
            "Blue Badge exemption (important)",
            "FREE for up to 30 minutes.",
            "Must pre-register the vehicle.",
            "Applies to private cars and taxis.",
            "AutoPay must be disabled or it will still charge.",
            "This is your FREE GOLD path for Gatwick."
          ];
        } else {
          warnings.unshift("Forecourt stay over 30 minutes risks a £100 PCN (reduced to £60 if paid within 14 days).");
          warnings.unshift("£10 for up to 10 minutes, then £1 for each additional minute up to 20 minutes.");
          warnings.unshift("Maximum daily charge: £30. Maximum stay: 30 minutes. Pay by midnight the day after using the drop-off zones (minimum charge £10 applies).");
        }
      }
      if (id === "PARK_RIDE") {
        warnings.unshift("Free drop-off + shuttle to terminal.");
      }
      if (id === "DROPOFF_ZONE" && isExempt) {
        infoNotes.push("♿ Accessibility priority: Blue Badge / exempt vehicles can use the forecourt.");
        infoNotes.push("ℹ️ Blue Badge holders are exempt from standard drop-off time limits at this terminal. Display badge clearly.");
      }
      return {
        ...raw,
        id,
        label: optionLabel(id),
        tagline: optionTagline(id),
        cost: Number.isFinite(Number(raw.cost)) ? Number(raw.cost) : null,
        warnings,
        infoNotes,
        isExempt: id === "DROPOFF_ZONE" && isExempt
      };
    };

    const dropoff = baseOption("DROPOFF_ZONE");
    const park = baseOption("PARK_RIDE");
    const shortStay = baseOption("SHORT_STAY");
    // Gatwick Short Stay pricing bands
    if (shortStay) {
      const mins = numMinutes;
      let price = null;
      if (mins <= 30) price = 8;
      else if (mins <= 60) price = 14;
      else if (mins <= 120) price = 18;
      else if (mins <= 180) price = 21.5;
      else if (mins <= 240) price = 26;
      else if (mins <= 300) price = 31.5;
      else if (mins <= 360) price = 40.5;
      else if (mins <= 720) price = 60;
      else if (mins <= 1440) price = 70;
      else {
        const extraDays = Math.ceil((mins - 1440) / 1440);
        price = 70 + extraDays * 70;
      }
      shortStay.cost = price;
    }

    // Gatwick Long Stay pricing bands
    if (park) {
      const mins = numMinutes;
      let price = 0;
      if (mins <= 120) {
        price = 0;
      } else if (mins <= 1440) {
        price = 38;
      } else {
        const extraDays = Math.ceil((mins - 1440) / 1440);
        price = 38 + extraDays * 32;
      }
      park.cost = price;
      const shuttleNote = "Requires shuttle transfer (10–12 mins each way) — slower than Short Stay for quick visits.";
      let infoNotes = Array.isArray(park.infoNotes) ? [...park.infoNotes] : [];
      if (park.cost === 0 || mins <= 30) {
        infoNotes.push(shuttleNote);
      }
      park.infoNotes = Array.from(new Set(infoNotes));
    }

    const medalDecision = () => {
      let copy = "Cheapest option overall.";

      const freeAvailable = (park?.cost === 0) || dropoff?.isExempt;

    if (dropoff?.isExempt) {
        const cheapestOther =
          [park, shortStay]
            .filter(Boolean)
            .sort((a, b) => (a.cost ?? Number.POSITIVE_INFINITY) - (b.cost ?? Number.POSITIVE_INFINITY))[0] || park || shortStay || null;
        const remaining =
          [park, shortStay].filter((o) => o && o.id !== cheapestOther?.id)[0] || (!cheapestOther && park) || (!cheapestOther && shortStay) || dropoff;
        copy = "Blue Badge exemption active — forecourt kept as Gold.";
        return {
          gold: dropoff,
          silver: cheapestOther || dropoff,
          bronze: remaining || dropoff,
          copy,
          freeAvailable
        };
      }

      // 0-10: drop-off gold, short silver, park bronze
      if (numMinutes > 0 && numMinutes <= 10) {
        copy = "Best for quick stops under 10 minutes.";
        return {
          gold: dropoff || shortStay || park,
          silver: shortStay || dropoff || park,
          bronze: park || shortStay || dropoff,
          copy,
          freeAvailable
        };
      }

      // 10-30: short gold, park silver, drop-off bronze
      if (numMinutes > 10 && numMinutes <= 30) {
        copy = "Short Stay wins on price + convenience; forecourt cost rises fast.";
        return {
          gold: shortStay || park || dropoff,
          silver: park || shortStay || dropoff,
          bronze: dropoff || shortStay || park,
          copy,
          freeAvailable
        };
      }

      // 30-120: park gold, short silver, drop-off bronze
      if (numMinutes > 30 && numMinutes <= 120) {
        copy = "Long Stay wins on price (often free) for this duration.";
        return {
          gold: park || shortStay || dropoff,
          silver: shortStay || park || dropoff,
          bronze: dropoff || shortStay || park,
          copy,
          freeAvailable
        };
      }

      // 120+: short gold, park silver, drop-off bronze
      copy = "Short Stay wins for longer stays; Long Stay is next best.";
      return {
        gold: shortStay || park || dropoff,
        silver: park || shortStay || dropoff,
        bronze: dropoff || shortStay || park,
        copy,
        freeAvailable
      };
    };

    const { gold, silver, bronze, copy: decisionCopy } = medalDecision();
    const drop = dropoff;
    const altCandidates = [park, shortStay].filter(Boolean).sort((a, b) => (a.cost ?? Infinity) - (b.cost ?? Infinity));
    const bestAlt = altCandidates.find((o) => o?.id !== drop?.id) || altCandidates[0] || null;
    const freeOption = [park, dropoff, shortStay].find((o) => o && (o.cost === 0 || o.isExempt)) || null;

    const recommendedId = (() => {
      if (drop?.isExempt || drop?.cost === 0) return "DROPOFF_ZONE";
      if (numMinutes <= 10) return "DROPOFF_ZONE";
      if (numMinutes <= 20) return shortStay ? "SHORT_STAY" : "DROPOFF_ZONE";
      if (numMinutes <= 30) return shortStay ? "SHORT_STAY" : park ? "PARK_RIDE" : "DROPOFF_ZONE";
      // >30 mins: avoid drop-off; prefer Short Stay if price is close (within £10) to Park & Ride to avoid shuttle hassle
      const shortCost = shortStay?.cost;
      const parkCost = park?.cost;
      if (shortCost != null && parkCost != null) {
        if (shortCost <= parkCost + 10) return "SHORT_STAY";
        return parkCost < shortCost ? "PARK_RIDE" : "SHORT_STAY";
      }
      if (shortStay) return "SHORT_STAY";
      if (park) return "PARK_RIDE";
      return "DROPOFF_ZONE";
    })();

    const displayTerminal = (() => {
      const raw = form.terminal || result?.journey?.terminal;
      const t = String(raw || "").toUpperCase();
      if (t.startsWith("N")) return "North";
      if (t.startsWith("S")) return "South";
      return raw || "—";
    })();

    const minsLabel = Number.isFinite(numMinutes) && numMinutes > 0 ? `${numMinutes} mins` : "your time";

    const freeOptions = [];
    if (park?.cost === 0) freeOptions.push(park.label || "Park & Ride");
    if (dropoff?.isExempt || dropoff?.cost === 0) freeOptions.push(dropoff?.label || "Terminal drop-off");
    const freeCallout =
      freeOptions.length === 0
        ? "❌ No free alternative available right now"
        : `🆓 Free alternative available (${freeOptions[0]}${park?.cost === 0 ? " up to 2h" : ""})`;

    const heroColors = {
      bg: "#0b1224",
      text: "#fff",
      border: "1px solid #0f172a",
      shadow: "0 10px 25px rgba(0,0,0,0.16)"
    };

    const toneStyles = {
      dropoff: { bg: "#fef3c7", text: "#78350f", border: "#fcd34d" },
      short: { bg: "#e0f2fe", text: "#0f172a", border: "#bae6fd" },
      park: { bg: "#ecfdf3", text: "#065f46", border: "#bbf7d0" }
    };

    const cardBlocks = [
      drop ? { opt: drop, title: "Terminal drop-off (Forecourt)", tone: "dropoff" } : null,
      shortStay ? { opt: shortStay, title: "Short Stay parking (walkable)", tone: "short" } : null,
      park ? { opt: park, title: "Park & Ride / Long Stay (free up to 2h)", tone: "park" } : null
    ]
      .filter(Boolean)
      .map((c) => ({ ...c, recommended: c.opt?.id === recommendedId }));

    const dropCostText = drop?.cost != null ? (drop.cost === 0 ? "FREE" : money(drop.cost)) : "—";
    const pcnRisk = numMinutes > 30 ? "⚠️ Yes if over 30 mins" : "Pay by midnight next day";

    return (
      <div
        style={{
          borderRadius: 18,
          padding: 18,
          marginBottom: 16,
          color: heroColors.text,
          background: heroColors.bg,
          border: heroColors.border,
          boxShadow: heroColors.shadow
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 14, letterSpacing: 0.4, textTransform: "uppercase", opacity: 0.9 }}>
          Gatwick Drop-off Charge (2026)
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>
          {numMinutes > 30
            ? `Gatwick drop-off zone not suitable for ${minsLabel} (max 30 mins)`
            : `Gatwick drop-off charge for ${minsLabel}: ${dropCostText}`}
        </div>
        <div style={{ fontSize: 14, marginTop: 6, opacity: 0.95, fontWeight: 800 }}>
          {(() => {
            if (recommendedId === "DROPOFF_ZONE") return `Recommended: Use Terminal drop-off (${dropCostText || "£—"})`;
            if (recommendedId === "SHORT_STAY")
              return `Recommended: Use Short Stay (${shortStay?.cost === 0 ? "FREE" : money(shortStay?.cost)})`;
            if (recommendedId === "PARK_RIDE") return "Recommended: Use Long Stay (FREE up to 2h)";
            return "Recommended option based on your stay";
          })()}
        </div>
        <div
          style={{
            marginTop: 12,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 10
          }}
        >
          {cardBlocks.map(({ opt, title, tone, recommended }, idx) => {
            const paletteFor =
              tone === "dropoff" ? toneStyles.dropoff : tone === "park" ? toneStyles.park : toneStyles.short;
            return (
              <div
                key={`${tone}-${idx}`}
                style={{
                  border: `1px solid ${paletteFor.border}`,
                  borderRadius: 14,
                  padding: 14,
                  background: paletteFor.bg,
                  color: paletteFor.text,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  minHeight: 140
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.3, display: "flex", alignItems: "center", gap: 6 }}>
                  {title}
                  {recommended && (
                    <span style={{ fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: "#0f172a", color: "#fff" }}>
                      Recommended
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 17, fontWeight: 800 }}>{opt?.label || "Awaiting option"}</div>
                <div style={{ fontSize: 16, fontWeight: 800 }}>
                  {opt?.cost != null ? (opt.cost === 0 ? "FREE" : money(opt.cost)) : "—"}
                </div>
                <div style={{ fontSize: 12, color: paletteFor.text, lineHeight: 1.4 }}>
                  {opt?.tagline || "Compare safety, speed, and price."}
                </div>
                {Array.isArray(opt?.warnings) && opt.warnings[0] && (
                  <div style={{ fontSize: 12 }}>{`⚠️ ${opt.warnings[0]}`}</div>
                )}
                {Array.isArray(opt?.infoNotes) &&
                  opt.infoNotes.map((n, idx2) => (
                    <div key={idx2} style={{ fontSize: 12 }}>
                      {n}
                    </div>
                  ))}
                <div style={{ marginTop: "auto" }}>
                  <button
                    type="button"
                    onClick={() => {
                      const map = {
                        DROPOFF_ZONE: "dropoff_zone",
                        SHORT_STAY: "short_stay",
                        PARK_RIDE: "park_ride"
                      };
                      const key = map[opt?.id];
                      if (!key) return;
                      const el = document.getElementById(`collapse-${key}`);
                      if (el) {
                        el.open = true;
                        requestAnimationFrame(() => {
                          el.scrollIntoView({ behavior: "smooth", block: "start" });
                        });
                      }
                    }}
                    style={{
                      marginTop: 8,
                      background: "transparent",
                      border: `1px solid ${paletteFor.border}`,
                      color: paletteFor.text,
                      padding: "6px 10px",
                      borderRadius: 10,
                      cursor: "pointer",
                      fontWeight: 700,
                      fontSize: 12
                    }}
                  >
                    View details ▸
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 12 }}>
          {result?.taxi_alternative && (
            <div style={{ padding: 10, borderRadius: 10, border: "1px solid #e5e7eb", background: "#f8fafc", color: "#0f172a" }}>
              <div style={{ fontWeight: 700, marginBottom: 2, fontSize: 12 }}>💡 {result.taxi_alternative.title}</div>
              <div style={{ fontSize: 12, color: "#374151" }}>
                {result.taxi_alternative.message}{" "}
                <a
                  href={result.taxi_alternative.cta || "/heathrow-fare-compare"}
                  style={{ fontWeight: 700, color: "#0f172a", textDecoration: "underline" }}
                >
                  Compare taxi fares →
                </a>
              </div>
            </div>
          )}

        <div style={{ fontSize: 12, color: heroColors.text, opacity: heroColors.text === "#fff" ? 0.85 : 0.9, marginTop: 6 }}>
            Terminal: <strong>{displayTerminal}</strong> • Type: <strong>{result.journey?.journey_type}</strong> • Planned time:{" "}
            <strong>{result.journey?.planned_minutes} mins</strong>
            {result.journey?.overheight ? " • Overheight" : ""}
            {result.journey?.blue_badge ? " • Blue Badge" : ""}
          </div>
        </div>
      </div>
    );
  };

  const renderOptionCard = (opt) => {
    const isBest = result?.recommendation?.best === opt.id;

    const linkBlocks = {
      DROPOFF_ZONE: [
        { label: "Pay Gatwick drop-off charge", href: "https://postpay.dropoff.gatwickairport.com/?customKey=33095c6292f04fc29ec34229fdd6ce20&_ga=2.156913656.2147047811.1620373886-1061217672.1614792929" },
        { label: "Pay or appeal a PCN", href: "https://www.ncp.co.uk/help-centre/pcn-payment-and-appeals/" },
        { label: "Blue Badge registration", href: "https://gatwick-app.zatpermit.com/applicant" },
        { label: "Pay by phone: 0330 174 4503", href: "tel:03301744503" }
      ],
      PARK_RIDE: [
        { label: "Long Stay / Park & Ride info", href: "https://www.gatwickairport.com/parking/parking-long-stay.html" }
      ],
      SHORT_STAY: [
        { label: "Short Stay parking prices", href: "https://www.gatwickairport.com/parking/roll-up-car-park-prices.html" }
      ]
    };

    return (
      <div
        key={opt.id}
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 14,
          marginBottom: 12,
          background: isBest ? "#ecfdf5" : "#fff"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ fontWeight: 800 }}>
            {opt.title}
            {isBest && badge("Recommended", "ok")}
          </div>
          <div style={{ fontWeight: 800 }}>{money(opt.cost)}</div>
        </div>

        {opt.best_for?.length > 0 && (
          <div style={{ marginTop: 6, fontSize: 13, color: "#374151" }}>
            Best for:{" "}
            <span style={{ color: "#111827" }}>
              {opt.best_for.includes("free_dropoff") ? "free / cheapest, " : ""}
              {opt.best_for.includes("pickup_best") ? "pick-ups, " : ""}
              {opt.best_for.includes("fastest") ? "fastest, " : ""}
              {opt.best_for.includes("waiting") ? "waiting, " : ""}
              {opt.best_for.includes("meet_and_greet_style") ? "meet & greet style, " : ""}
            </span>
          </div>
        )}

        {Array.isArray(opt.warnings) && opt.warnings.length > 0 && (
          <div style={{ marginTop: 10, background: "#fff7ed", border: "1px solid #fed7aa", padding: 10, borderRadius: 10 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>⚠️ Important (Gatwick drop-off rules)</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              {opt.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}

        {Array.isArray(opt.notes) && opt.notes.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5, color: "#374151" }}>
              {opt.notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          </div>
        )}

        {linkBlocks[opt.id] && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Next steps</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              {linkBlocks[opt.id].map((link, i) => (
                <li key={i}>
                  <a href={link.href} target="_blank" rel="noreferrer">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderOptionSection = (opt, title, defaultOpen = false) => {
    if (!opt) return null;
    return (
      <details id={`collapse-${opt.id.toLowerCase().replace(/_/g, "_")}`} className="train-collapsible" open={defaultOpen} style={{ marginTop: 12 }}>
        <summary className="train-summary-bar">{title}</summary>
        <div className="train-collapsible-body">{renderOptionCard(opt)}</div>
      </details>
    );
  };

  return (
    <div className="parking-wrapper">
      <Helmet>
        <title>Gatwick Drop-off (2026): Live Fees &amp; How to Pay</title>

        <meta
          name="description"
          content="Gatwick drop-off charges explained for 2026. See the £10 fee, free Long Stay options, how to pay, Blue Badge rules, and how to avoid £100 PCNs."
        />

        <meta
          name="keywords"
          content="Gatwick drop off charge 2026, Gatwick drop off fee, Gatwick free drop off, Gatwick short stay prices, Gatwick long stay free 2 hours, Gatwick forecourt PCN"
        />

        <link rel="canonical" href="https://www.cheapcabgrab.com/gatwick-dropoff" />

        <meta property="og:title" content="Gatwick Drop-off (2026): Live Fees &amp; How to Pay" />
        <meta
          property="og:description"
          content="Gatwick drop-off charges explained for 2026. See the £10 fee, free Long Stay options, how to pay, Blue Badge rules, and how to avoid £100 PCNs."
        />
        <meta property="og:url" content="https://www.cheapcabgrab.com/gatwick-dropoff" />

        {/* FAQ Rich Snippet */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "How much is the Gatwick terminal drop-off charge?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Gatwick forecourt drop-off is £10 for up to 10 minutes, then £1 per additional minute up to 20 minutes. Maximum daily charge is £30. Pay online/phone by midnight the next day."
                }
              },
              {
                "@type": "Question",
                name: "Is there a free drop-off option at Gatwick?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Use Gatwick Long Stay car parks for free drop-off up to the posted time limit (usually 2 hours) with a shuttle to the terminal."
                }
              },
              {
                "@type": "Question",
                name: "What is the Gatwick drop-off penalty?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Forecourt drop-off can lead to a £100 Parking Charge Notice if unpaid or overstayed; discounted to £60 if paid within 14 days. Always pay by midnight the next day."
                }
              },
              {
                "@type": "Question",
                name: "Should I use Short Stay parking for pick-ups at Gatwick?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Yes. For pick-ups or waiting, use Short Stay car parks. Forecourt drop-off is not suitable for waiting."
                }
              },
              {
                "@type": "Question",
                name: "How much is Gatwick Short Stay parking for a few hours?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Short Stay prices vary by duration. This guide compares your planned time to Short Stay tariffs and surfaces the safest option for your stay."
                }
              },
              {
                "@type": "Question",
                name: "Do taxis and private-hire vehicles pay the Gatwick drop-off charge?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Yes. Taxis and private-hire vehicles pay the standard forecourt charge unless a discount/exemption applies."
                }
              },
              {
                "@type": "Question",
                name: "Can Blue Badge holders get a discount for Gatwick drop-off?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Blue Badge concessions can apply at Gatwick (check current rules). Always register/confirm via official Gatwick channels."
                }
              },
              {
                "@type": "Question",
                name: "What does this Gatwick drop-off calculator check?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "It compares the Gatwick forecourt charge against Long Stay (free option) and Short Stay based on your planned time, and highlights payment deadlines and exemptions."
                }
              }
            ]
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Gatwick Drop-off Guide (Forecourt vs Short Stay vs Long Stay)",
            url: "https://www.cheapcabgrab.com/gatwick-dropoff",
            description:
              "Plan Gatwick forecourt drop-off vs Long Stay (free up to 2 hours) vs Short Stay parking with pricing bands, rules, and PCN deadlines.",
            inLanguage: "en-GB",
            mainEntity: [
              {
                "@type": "Question",
                name: "Is Gatwick drop-off free anywhere?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Free drop-off is available at Gatwick Long Stay car parks within posted time limits, with a shuttle to the terminal. This tool compares your options by duration."
                }
              },
              {
                "@type": "Question",
                name: "What happens if I stay longer at Gatwick drop-off?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Overstaying or not paying the Gatwick forecourt charge can lead to enforcement penalties. This tool highlights the risk based on your planned time."
                }
              },
              {
                "@type": "Question",
                name: "Where should I pick up passengers at Gatwick?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Pick-ups are usually made from Short Stay or Long Stay, not the terminal forecourt. This tool recommends the best option for pick-ups."
                }
              }
            ]
          })}
        </script>

        {/* Software Application schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Airport Drop-off Price Engine",
            applicationCategory: "TravelApplication",
            operatingSystem: "All",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "GBP"
            },
            description:
              "Dynamic calculator for UK airport drop-off fees. Re-ranks parking options in real-time based on minutes stayed."
          })}
        </script>
      </Helmet>

      <LiveStrip variant="gatwick" />

      <div className="parking-header">
        <h1>🚗 Gatwick Drop-off Guide (Forecourt vs Short Stay vs Long Stay)</h1>
        <p>
          Compare <strong>Gatwick forecourt drop-off charge</strong>, <strong>Short Stay price bands</strong>, and{" "}
          <strong>Long Stay (free up to 2 hours)</strong>. Includes Blue Badge handling, penalty risk, and shuttle info.
        </p>
        <p style={{ fontSize: 14, color: "#374151" }}>
          Check all options to drop off. <a href="/gatwick-dropoff-calculator">Use the drop-off calculator.</a>
        </p>
      </div>

      {/* FORM */}
      <div className="parking-form">
        <div className="toggle-stack" style={{ marginBottom: 8 }}>
          <div className="toggle-grid">
            <label className={`toggle-pill ${form.blue_badge ? "active" : ""}`}>
              <input
                type="checkbox"
                checked={form.blue_badge}
                onChange={(e) => setForm({ ...form, blue_badge: e.target.checked, blue_badge_registered: false })}
              />
              <span>Blue Badge holder</span>
            </label>

            {form.blue_badge && (
              <label className={`toggle-pill ${form.blue_badge_registered ? "active" : ""}`}>
                <input
                  type="checkbox"
                  checked={form.blue_badge_registered}
                  onChange={(e) => setForm({ ...form, blue_badge_registered: e.target.checked })}
                />
                <span>Trip registered / approved</span>
              </label>
            )}
          </div>
        </div>

        <div className="minutes-card" style={{ marginTop: 4 }}>
          <div className="minutes-label-row">
            <label style={{ margin: 0 }} htmlFor="planned_minutes">Duration of stay (mins)</label>
            <span className="minutes-chip">Time</span>
          </div>
          <div className="minutes-input-row">
            <input
              type="number"
              className="parking-input"
              min="1"
              max="10080"
              step="1"
              id="planned_minutes"
              value={form.planned_minutes}
              onChange={(e) =>
                setForm({
                  ...form,
                  planned_minutes: e.target.value === "" ? "" : Math.min(10080, Math.max(1, Number(e.target.value)))
                })
              }
              onBlur={() => {
                setForm({ ...form, planned_minutes: clampMinutes(form.planned_minutes) });
              }}
            />
            <div className="minutes-nudge">
              <button
                type="button"
                onClick={() => setForm({ ...form, planned_minutes: clampMinutes((Number(form.planned_minutes) || 0) - 1) })}
              >
                −
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, planned_minutes: clampMinutes((Number(form.planned_minutes) || 0) + 1) })}
              >
                +
              </button>
            </div>
          </div>
        </div>

        {error && <p style={{ color: "red", marginTop: 10 }}>{error}</p>}
      </div>

      {/* RESULTS */}
      {result && (
        <div className="parking-results">
          {renderMedalHero()}

          {(() => {
            const map = {};
            (Array.isArray(result.options) ? result.options : []).forEach((o) => {
              map[o.id] = o;
            });
            const drop = map["DROPOFF_ZONE"] || null;
            const park = map["PARK_RIDE"] || null;
            const shortStay = map["SHORT_STAY"] || null;

            if (drop) {
              const outdatedDropNotes = new Set([
                "£7 is charged each time a vehicle enters a terminal drop-off zone (per visit).",
                "If paying after, you must pay by midnight the next day.",
                "A 10-minute maximum stay applies on terminal forecourts (PCN risk if exceeded)."
              ]);
              drop.notes = Array.isArray(drop.notes) ? drop.notes.filter((n) => !outdatedDropNotes.has(n)) : [];
              drop.warnings = [
                "Forecourt stay over 30 minutes risks a £100 Parking Charge Notice (PCN) (reduced to £60 if paid within 14 days).",
                "£10 for up to 10 minutes, then £1 for each additional minute up to 20 minutes.",
                "Maximum daily charge: £30.",
                "Maximum length of stay: 30 minutes on terminal forecourts.",
                "Payment must be made by midnight the day after using the drop-off zones (minimum charge £10 applies).",
                "Failure to pay on time will result in a £100 PCN, reduced to £60 if paid within 14 days."
              ];
            }

            if (park) {
              park.best_for = ["free_dropoff", "waiting"];
              park.warnings = [];
              park.notes = [
                "Best for free drop-off (up to 2 hours) or longer stays.",
                "Free for up to 2 hours when picking up or dropping off.",
                "£38 for the first day, £32 per day after (cheaper if pre-booked).",
                "Free shuttle bus every 10–12 minutes, runs 24/7.",
                "10–12 minute transfer to North or South Terminal.",
                "How it works: Drive to Long Stay → number plate is scanned → barrier opens automatically.",
                "Park in any available bay (family bays available near bus stops).",
                "Take the free shuttle directly to the terminal. Shuttle buses run day and night back to Long Stay.",
                "Simply exit the car park — payment applies only if you stay over the free period."
              ];
            }

            return (
              <>
                {(() => {
                  const altCandidates = [park, shortStay].filter(Boolean).sort((a, b) => (a.cost ?? Infinity) - (b.cost ?? Infinity));
                  const bestAlt = altCandidates.find((o) => o?.id !== drop?.id) || altCandidates[0] || null;
                  const freeOption = [park, shortStay, drop].find((o) => o && (o.cost === 0 || o.isExempt)) || null;
                  const detailOrder = [drop, shortStay, park].filter(Boolean);
                  return detailOrder.map((o) =>
                    renderOptionSection(
                      o,
                      o.id === "DROPOFF_ZONE"
                        ? "Terminal drop-off"
                        : o.id === "SHORT_STAY"
                        ? "Short Stay parking"
                        : "Park & Ride / Long Stay",
                      false
                    )
                  );
                })()}
              </>
            );
          })()}

          {Array.isArray(result.notices) && result.notices.length > 0 && (
            <div style={{ marginTop: 12, background: "#f9fafb", border: "1px solid #e5e7eb", padding: 12, borderRadius: 12 }}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>ℹ️ Notes</div>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
                {result.notices.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            </div>
          )}

          <details className="train-collapsible">
            <summary className="train-summary-bar">ℹ️ What this tool checks (Gatwick)</summary>
            <div className="train-collapsible-body">
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
                <li>Terminal drop-off charges vs Long Stay (Park &amp; Ride) vs Short Stay parking.</li>
                <li>Time-based pricing in Gatwick drop-off zones (10–30 minute bands).</li>
                <li>PCN risk after 30 minutes or unpaid drop-off sessions.</li>
                <li>Blue Badge exemptions (when registered in advance).</li>
                <li>Vehicle-type rules (private car, taxi/private hire, exempt vehicles).</li>
                <li>Free options (e.g. Long Stay up to 2 hours where applicable).</li>
                <li>This tool highlights when a free option is available and clearly shows the best paid alternative when it isn’t.</li>
              </ul>
            </div>
          </details>

          <details className="train-collapsible" style={{ marginTop: 12 }}>
            <summary className="train-summary-bar">🆘 Need help with Gatwick drop-off or parking?</summary>
            <div className="train-collapsible-body">
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                <li>💳 Pay Gatwick drop-off charge – <a href="https://postpay.dropoff.gatwickairport.com/" target="_blank" rel="noreferrer">postpay.dropoff.gatwickairport.com</a></li>
                <li>🧾 Pay or appeal a PCN (NCP) – <a href="https://www.ncp.co.uk/help-centre/pcn-payment-and-appeals/" target="_blank" rel="noreferrer">ncp.co.uk/help-centre/pcn-payment-and-appeals/</a></li>
                <li>♿ Blue Badge exemption registration – <a href="https://gatwick-app.zatpermit.com/applicant" target="_blank" rel="noreferrer">gatwick-app.zatpermit.com/applicant</a></li>
                <li>📞 Pay by phone: <a href="tel:03301744503">0330 174 4503</a></li>
                <li>📞 General parking help: <a href="tel:03450507080">0345 050 7080</a> (Option 2 → Option 1)</li>
              </ul>
            </div>
          </details>

          {/* Internal links (same pattern as Train tool) */}
          <details className="train-collapsible" style={{ marginTop: 12 }}>
            <summary className="train-summary-bar">✅ Why this Gatwick calculator is reliable</summary>
            <div className="train-collapsible-body">
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#f9fafb" }}>
                <p style={{ marginTop: 0, marginBottom: 6, fontSize: 14, color: "#374151" }}>
                  This calculator uses official published rules and pricing from London Gatwick Airport and its parking operator to compare:
                </p>
                <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5, fontSize: 13, color: "#374151" }}>
                  <li>Terminal drop-off charges (time-based).</li>
                  <li>Short Stay and Long Stay (Park &amp; Ride) pricing.</li>
                  <li>Free eligibility based on duration, vehicle type, and Blue Badge status.</li>
                  <li>PCN risk thresholds and payment deadlines.</li>
                </ul>
                <div style={{ marginTop: 8, fontSize: 13, color: "#374151", fontWeight: 700 }}>What makes it useful</div>
                <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5, fontSize: 13, color: "#374151" }}>
                  <li>Updated to reflect current Gatwick pricing bands and limits.</li>
                  <li>Flags when a “free” option disappears as stay time increases.</li>
                  <li>Explains why one option becomes Gold / Silver / Bronze.</li>
                  <li>Designed to surface the cheapest safe option, not just the lowest headline price.</li>
                </ul>
                <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
                  Rules and prices can change. Always check official Gatwick signage and payment pages before travel.
                </div>
              </div>
            </div>
          </details>

          <details className="train-collapsible" style={{ marginTop: 12 }}>
            <summary className="train-summary-bar">Why Gatwick Long Stay can be free</summary>
            <div className="train-collapsible-body">
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
                <li>Long Stay (Park &amp; Ride) is free for up to 2 hours for pick-up/drop-off.</li>
                <li>Free shuttle buses run every 10–12 minutes, 24/7, to North and South terminals.</li>
                <li>After 2 hours, charges apply (£38 first day, then £32 per day unless pre-booked discounts apply).</li>
              </ul>
            </div>
          </details>

          <div style={{ marginTop: 14, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#fff" }}>
            <strong>🔗 Official Gatwick references (used by this tool)</strong>
            <ul style={{ marginTop: 8, lineHeight: 1.6, marginBottom: 0 }}>
              <li>Drop-off charges &amp; payment: <a href="https://www.gatwickairport.com/parking/drop-off/" target="_blank" rel="noreferrer">gatwickairport.com/parking/drop-off/</a></li>
              <li>Roll-up parking prices: <a href="https://www.gatwickairport.com/parking/roll-up-car-park-prices.html" target="_blank" rel="noreferrer">gatwickairport.com/parking/roll-up-car-park-prices.html</a></li>
              <li>Long Stay parking: <a href="https://www.gatwickairport.com/parking/parking-long-stay.html" target="_blank" rel="noreferrer">gatwickairport.com/parking/parking-long-stay.html</a></li>
            </ul>
            <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
              Last updated: {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 40 }}>
        <ReviewsBar />
      </div>
    </div>
  );
}
